import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Empty, Input, message, Modal, Result, Switch, Tooltip } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import { filterByName, pagedGroups } from './eligibilityGroups';

import { getEligibility, recordEligibilityAudit, setCanTenantBind } from '../../../../hooks/interoperability';
import { EligibilityAccount } from '../../../../types/responses/Interoperability';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { countLabel } from '../listPaging';
import { FilterBar, FilterCount, FilterSpacer } from '../styled';

const Card = styled.div`
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  padding: 20px 24px;
`;

const Title = styled.h3`
  font-size: 15px;
  color: #12263f;
  margin: 0 0 4px;
`;

const Lead = styled.p`
  color: #5b6472;
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 14px;
  max-width: 820px;
`;

/* Aviso de que ligar compartilha dados — a ação é sensível. */
const Notice = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #fdf6ec;
  border: 1px solid #f5e2c0;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 18px;
  color: #8a5a17;
  font-size: 12.5px;
  line-height: 1.5;
  max-width: 820px;

  .anticon {
    margin-top: 2px;
    color: #cf8a2e;
  }
`;

const Group = styled.section`
  border: 1px solid #eef0f3;
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f7f9fc;
  border-bottom: 1px solid #eef0f3;
`;

const GroupName = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  font-weight: 600;
`;

const CountPill = styled.span`
  color: #6b7280;
  background: #eef0f2;
  border-radius: 10px;
  padding: 0 8px;
  font-size: 11px;
  line-height: 18px;
`;

const GroupSpacer = styled.span`
  flex: 1;
`;

const Row = styled.div<{ $depth: number; $on: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  padding-left: ${({ $depth }) => 12 + $depth * 20}px;
  border-bottom: 1px solid #f5f6f8;
  font-size: 13px;
  color: #12263f;
  border-left: 3px solid ${({ $on, theme }) => ($on ? theme.primary : 'transparent')};
  background: ${({ $on }) => ($on ? '#f4f8ff' : '#fff')};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $on }) => ($on ? '#eaf2ff' : '#f9fafb')};
  }

  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }
`;

/* Botão de link no tema: o antd deixa o type="link" no azul padrão, fora do tema. */
const LinkAction = styled(Button)`
  &.ant-btn-link {
    color: ${({ theme }) => theme.primary};
    padding: 0;
    height: auto;
  }

  &.ant-btn-link:hover,
  &.ant-btn-link:focus {
    color: ${({ theme }) => theme.primary};
    opacity: 0.8;
  }
`;

/* Toggle no tema do dashboard: o antd deixa o Switch ligado no azul padrão, fora do tema. */
const ThemedSwitch = styled(Switch)`
  &.ant-switch-checked {
    background-color: ${({ theme }) => theme.primary};
  }

  &.ant-switch-checked:hover:not(.ant-switch-disabled) {
    background-color: ${({ theme }) => theme.primary};
    opacity: 0.9;
  }

  &.ant-switch:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primary}33;
  }
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StationPill = styled.span`
  color: #5b6472;
  background: #f0f2f5;
  border-radius: 10px;
  padding: 1px 9px;
  font-size: 11px;
  white-space: nowrap;
`;

const StateTag = styled.span<{ $on: boolean }>`
  font-size: 11px;
  min-width: 78px;
  text-align: right;
  color: ${({ $on, theme }) => ($on ? theme.primary : '#98a1ad')};
`;

/* Barra de ação fixa no rodapé da aba. */
const Bar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #eef0f3;
  background: #fff;
`;

const Summary = styled.span`
  color: #5b6472;
  font-size: 13px;
  margin-right: auto;
`;

const SummaryBreak = styled.span`
  color: #8d929e;
  font-size: 12px;
  margin-left: 8px;
`;

const levelLabel = (level: number): string => {
  switch (level) {
    case 0:
      return TF('This account');
    case 1:
      return TF('Children');
    case 2:
      return TF('Grandchildren');
    default:
      return `${TF('Level')} ${level}`;
  }
};

/* Rodapé da seção: a contagem da fatia à esquerda, o paginador à direita. */
const GroupFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-top: 1px solid #f5f6f8;
  background: #fcfdfe;

  .pagination-container {
    margin-top: 6px;
    padding-bottom: 6px;
    margin-left: auto;
  }
`;

const ShownCount = styled.span`
  color: #6b7280;
  font-size: 11.5px;
`;

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? TF(one) : TF(many)}`;

/**
 * Contas por página em cada nível (MV2-1812).
 *
 * Dez, como na árvore de escopo da aba Conexões. Uma rede grande enche o nível dos filhos
 * com dezenas de contas e empurra os níveis seguintes e a barra de salvar para fora da
 * tela — e a barra é o único lugar onde aparece o que está pendente.
 */
const PAGE_SIZE = 10;

export type EligibilityTabProps = {
  tenantId: string;
};

/**
 * Moderação da elegibilidade de interoperabilidade (can_tenant_bind) da conta e dos
 * descendentes, por nível (a conta, filhos, netos...). Ligar abre a replicação de dados do
 * tenant para o cross — por isso a aba fica atrás de permissão dedicada e a escrita vai ao CS
 * (fonte de verdade), que replica para o cross.
 */
export const EligibilityTab: React.FC<EligibilityTabProps> = ({ tenantId }) => {
  const [accounts, setAccounts] = useState<EligibilityAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  // O estado marcado da tela. O original vem do can_tenant_bind de cada conta.
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});

  /**
   * A página aberta em cada nível.
   *
   * Só recorta o desenho: o que se marca continua sendo guardado por tenant_id sobre a
   * lista inteira, então virar de página não apaga a marcação da anterior — ela vai junto
   * no salvar, e o rodapé continua contando tudo que está pendente.
   */
  const [pages, setPages] = useState<Record<number, number>>({});

  /**
   * Termo da busca por nome (MV2-1812).
   *
   * Com a lista paginada, a conta procurada quase nunca esta na pagina aberta — e uma rede
   * grande nao cabe na memoria de quem procura. A busca varre o conjunto inteiro, e nao a
   * pagina: filtrar o que ja esta na tela nao acharia nada do que importa.
   */
  const [busca, setBusca] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getEligibility();
      setAccounts(data);
      setEnabled(Object.fromEntries(data.map((a) => [a.tenant_id, a.can_tenant_bind])));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const original = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.tenant_id, a.can_tenant_bind])),
    [accounts],
  );

  const toEnable = useMemo(
    () => accounts.filter((a) => enabled[a.tenant_id] && !original[a.tenant_id]).map((a) => a.tenant_id),
    [accounts, enabled, original],
  );
  const toDisable = useMemo(
    () => accounts.filter((a) => !enabled[a.tenant_id] && original[a.tenant_id]).map((a) => a.tenant_id),
    [accounts, enabled, original],
  );
  const changeCount = toEnable.length + toDisable.length;

  /**
   * O que a busca deixou. E daqui que sai tudo o que a tela desenha: os niveis, a
   * paginacao de cada um e o alvo do "Habilitar todos" — com busca ativa, o nivel na tela
   * e o resultado da busca, e ligar o que nao esta a vista seria uma armadilha.
   *
   * O que esta PENDENTE continua sendo contado sobre a lista inteira (o `toEnable` e o
   * `toDisable` abaixo): marcacao feita antes de filtrar nao pode sumir do resumo so
   * porque a conta saiu da tela.
   */
  const visiveis = useMemo(
    () => filterByName(accounts, busca, T('This account')),
    [accounts, busca],
  );

  const groups = useMemo(() => pagedGroups(visiveis, pages, PAGE_SIZE), [visiveis, pages]);

  /** Buscar recomeca a paginacao: a pagina de antes pode nao existir no resultado. */
  const procurar = (termo: string) => {
    setBusca(termo);
    setPages({});
  };

  const toggle = (id: string) => setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleLevel = (items: EligibilityAccount[], value: boolean) =>
    setEnabled((prev) => {
      const next = { ...prev };
      items.forEach((a) => {
        next[a.tenant_id] = value;
      });
      return next;
    });

  const discard = () =>
    setEnabled(Object.fromEntries(accounts.map((a) => [a.tenant_id, a.can_tenant_bind])));

  const apply = async () => {
    setSaving(true);
    try {
      // Duas chamadas no máximo: um lote para ligar, outro para desligar. A auditoria no cross é
      // best-effort (a fonte de verdade é a escrita no CS) — não desfaz o sucesso se falhar.
      if (toEnable.length) {
        await setCanTenantBind(toEnable, true);
        recordEligibilityAudit(tenantId, toEnable, true).catch(() => undefined);
      }
      if (toDisable.length) {
        await setCanTenantBind(toDisable, false);
        recordEligibilityAudit(tenantId, toDisable, false).catch(() => undefined);
      }
      message.success(T('Eligibility updated.'));
      await load();
    } catch {
      message.error(T('Could not update eligibility. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (changeCount === 0) {
      message.info(T('No changes to save.'));
      return;
    }

    // Ligar abre replicação de dados — confirma o impacto antes. Desligar não precisa.
    if (toEnable.length) {
      const stations = accounts
        .filter((a) => toEnable.includes(a.tenant_id))
        .reduce((sum, a) => sum + (a.station_count || 0), 0);

      Modal.confirm({
        title: T('Enable eligibility for these accounts?'),
        icon: <ExclamationCircleOutlined />,
        content: `${TF('This shares their data with the cross network:')} ${plural(
          toEnable.length,
          'account',
          'accounts',
        )}, ${plural(stations, 'station', 'stations')}.${
          toDisable.length ? ` ${plural(toDisable.length, 'account', 'accounts')} ${TF('will be turned off.')}` : ''
        }`,
        okText: T('Enable'),
        cancelText: T('Cancel'),
        onOk: apply,
      });
      return;
    }

    apply();
  };

  if (loading) return <ListSkeleton />;

  if (loadError) {
    return (
      <Result
        status="warning"
        title={T('Could not load the accounts.')}
        extra={
          <Button type="primary" onClick={load}>
            {T('Try again')}
          </Button>
        }
      />
    );
  }

  return (
    <Card>
      <Title>{T('Interoperability eligibility')}</Title>
      <Lead>
        {T(
          'Enable which accounts can take part in interoperability. Enabling replicates that account stations and charges to the cross network — turn on only what should be shared.',
        )}
      </Lead>

      <Notice>
        <ApiOutlined />
        <span>
          {T(
            'Enabling an account shares its stations and charges with the cross network. Turn on only what should be visible to partners.',
          )}
        </span>
      </Notice>

      <FilterBar>
        <Input.Search
          allowClear
          style={{ maxWidth: 260 }}
          placeholder={TF('Search account by name')}
          value={busca}
          onChange={(event) => procurar(event.target.value)}
        />

        <FilterSpacer />

        {/*
          Contagem ao lado do campo: poucas linhas na tela podem ser poucas contas ou o
          corte da busca, e sem isto a busca esconde o proprio efeito.
        */}
        <FilterCount>
          {countLabel(visiveis.length, accounts.length, TF('accounts'), TF('of'))}
        </FilterCount>
      </FilterBar>

      {accounts.length === 0 && <Empty description={T('No accounts to show.')} />}

      {/*
        Nenhum resultado de busca e diferente de nenhuma conta: quem procurou precisa saber
        que existe conta fora do termo digitado.
      */}
      {accounts.length > 0 && visiveis.length === 0 && (
        <Empty description={T('No account matches this search.')} />
      )}

      {accounts.length > 0 && visiveis.length > 0 && (
        groups.map(({ level, items, visible, page, paginated }) => {
          const on = items.filter((a) => enabled[a.tenant_id]).length;
          const allOn = on === items.length;
          return (
            <Group key={level}>
              <GroupHead>
                <GroupName>{levelLabel(level)}</GroupName>
                {/* Conta o nível inteiro, e não a página: é o total que o botão ao lado move. */}
                <CountPill>{`${on}/${items.length}`}</CountPill>
                <GroupSpacer />
                {items.length > 1 && (
                  <LinkAction
                    type="link"
                    size="small"
                    // Com o nível paginado o botão mexe em conta que não está na tela; dizer
                    // isso antes do clique é mais barato que desfazer depois.
                    title={paginated ? T('Applies to every account on this level, including the other pages.') : undefined}
                    onClick={() => toggleLevel(items, !allOn)}
                  >
                    {allOn ? T('Clear all') : T('Enable all')}
                  </LinkAction>
                )}
              </GroupHead>

              {visible.map((a) => {
                const isOn = !!enabled[a.tenant_id];
                return (
                  <Row key={a.tenant_id} $depth={a.level} $on={isOn}>
                    <Name title={a.name || T('This account')}>{a.name || T('This account')}</Name>
                    <Tooltip title={`${a.station_count} ${TF('stations')}`}>
                      <StationPill>{a.station_count}</StationPill>
                    </Tooltip>
                    <StateTag $on={isOn}>{isOn ? TF('Enabled') : TF('Not shared')}</StateTag>
                    <ThemedSwitch
                      size="small"
                      checked={isOn}
                      onChange={() => toggle(a.tenant_id)}
                      aria-label={`${a.name} — ${isOn ? TF('Enabled') : TF('Not shared')}`}
                    />
                  </Row>
                );
              })}

              {/*
                O paginador some sozinho quando o nível cabe numa página, então hierarquia
                pequena não muda de aparência. A contagem ao lado diz que a seção mostra uma
                fatia: dez linhas podem ser dez contas ou o começo de setenta.
              */}
              {paginated && (
                <GroupFoot>
                  <ShownCount>
                    {countLabel(visible.length, items.length, TF('accounts'), TF('of'))}
                  </ShownCount>
                  <Pagination
                    currentPage={page}
                    pageSize={PAGE_SIZE}
                    totalCount={items.length}
                    onPageChange={(next: number) =>
                      setPages((current) => ({ ...current, [level]: next }))
                    }
                  />
                </GroupFoot>
              )}
            </Group>
          );
        })
      )}

      <Bar>
        <Summary>
          {changeCount === 0 ? (
            T('No pending changes.')
          ) : (
            <>
              {`${changeCount} ${TF('pending changes')}`}
              <SummaryBreak>
                {toEnable.length > 0 && `${toEnable.length} ${TF('to enable')}`}
                {toEnable.length > 0 && toDisable.length > 0 && ' · '}
                {toDisable.length > 0 && `${toDisable.length} ${TF('to disable')}`}
              </SummaryBreak>
            </>
          )}
        </Summary>

        {changeCount > 0 && (
          <Button type="text" disabled={saving} onClick={discard}>
            {T('Discard')}
          </Button>
        )}
        <Button type="primary" loading={saving} disabled={changeCount === 0} onClick={save}>
          {saving ? T('Saving…') : T('Save changes')}
        </Button>
      </Bar>
    </Card>
  );
};
